import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher";
import logo from '../../assets/new images/logo.png';

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const panelRef = useRef(null);

  const NAV_LINKS = [
    { label: t("nav.home"), href: "/" },
    { label: t("nav.aboutUs"), href: "/about-us" },
    {
      label: t("nav.mentorship"),
      dropdown: [
        { label: t("nav.findMentor"), href: "/find-mentor" },
        { label: t("nav.becomeMentor"), href: "/become-a-mentor" },
      ],
    },
    { label: t("nav.aiCareer"), href: "/ai-career" },
    {
      label: t("nav.resources"),
      dropdown: [
        { label: t("nav.blogs"), href: "/blog" },
        { label: t("nav.scholarships"), href: "/scholarship" },
        { label: t("nav.careerGuide"), href: "/career-guide" },
      ],
    },
    { label: t("nav.cabilKids"), href: "/kids", highlight: true },
    { label: t("nav.contactUs"), href: "/contact-us" },
  ];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    const onClick = (e) => {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const LinkItem = ({ label, href, highlight }) => (
    <a href={href}
      className={`block px-3 py-2 rounded-md transition ${highlight ? "bg-[#EE5F2B] text-white font-semibold hover:bg-orange-600" : "text-gray-700 hover:text-[#EE5F2B]"}`}
      onClick={() => setOpen(false)}>
      {label}
    </a>
  );

  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-white backdrop-blur border-b border-gray-100">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={logo} alt="" />
          </a>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            <ul className="flex items-center gap-6 text-gray-700 font-medium relative">
              {NAV_LINKS.map((l, idx) => {
                const key = l.href || `nav-${idx}`;
                return (
                  <li key={key} className="relative group"
                    onMouseEnter={() => l.dropdown ? setActiveDropdown(key) : setActiveDropdown(null)}
                    onMouseLeave={() => setActiveDropdown(null)}>
                    <Link to={l.href || "#"}
                      className={l.highlight
                        ? "bg-[#EE5F2B] text-white py-1.5 px-3 rounded-lg font-semibold flex items-center gap-1 hover:bg-orange-600 transition"
                        : "hover:bg-orange-50 hover:text-[#EE5F2B] py-1 px-1 rounded-lg transition flex font-light items-center gap-1"}>
                      {l.label}
                      {l.dropdown && (
                        <svg className="w-4 h-4 mt-[2px]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </Link>
                    {l.dropdown && activeDropdown === key && (
                      <div className="absolute left-0 w-48 bg-white border border-gray-100 shadow-lg rounded-lg py-2">
                        {l.dropdown.map((d) => (
                          <Link key={d.href} to={d.href}
                            className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-[#EE5F2B] transition">
                            {d.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center gap-2">
              <LanguageSwitcher variant="compact" />
              <Link to="/login" className="border border-[#EE5F2B] text-[#EE5F2B] px-4 py-2 rounded-lg hover:bg-orange-50 transition">
                {t("nav.login")}
              </Link>
              <Link to="/signup" className="bg-[#EE5F2B] text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition">
                {t("nav.signup")}
              </Link>
            </div>
          </div>

          {/* Mobile Hamburger + always-visible language switcher */}
          <div className="lg:hidden flex items-center gap-1.5">
            <LanguageSwitcher variant="compact" />
            <button type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#EE5F2B]"
              onClick={() => setOpen(true)}>
              <svg width="24" height="24" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>
      </div>

      <div className={`lg:hidden fixed inset-0 bg-black/30 transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} />

      <div className={`lg:hidden fixed right-0 top-0 h-screen w-[78%] max-w-sm bg-white shadow-xl transition-transform duration-300 will-change-transform overflow-y-auto ${open ? "translate-x-0" : "translate-x-full"}`}
        ref={panelRef} role="dialog" aria-modal="true">
        <div className="flex items-center justify-between px-4 pt-4">
          <img src={logo} alt="" />
          <button className="rounded-md p-2 text-gray-700 hover:bg-gray-100" onClick={() => setOpen(false)}>
            <svg width="24" height="24" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          </button>
        </div>

        <div className="px-4 pb-3 pt-2">
          <div className="mt-3 space-y-1">
            {NAV_LINKS.map((l) => (
              <div key={l.href}>
                <LinkItem {...l} />
                {l.dropdown && (
                  <div className="ml-3 border-l border-gray-200 pl-3">
                    {l.dropdown.map((d) => <LinkItem key={d.href} {...d} />)}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-2">
            <Link to="/login" onClick={() => setOpen(false)}
              className="w-full text-center border border-[#EE5F2B] text-[#EE5F2B] px-4 py-2 rounded-lg hover:bg-orange-50 transition">
              {t("nav.login")}
            </Link>
            <Link to="/signup" onClick={() => setOpen(false)}
              className="w-full text-center bg-[#EE5F2B] text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition">
              {t("nav.signup")}
            </Link>
          </div>

          <div className="mt-4 flex justify-center">
            <LanguageSwitcher variant="compact" />
          </div>

          <div className="mt-4 border-t pt-4 text-sm text-gray-500">
            <p>© 2025 Cabil</p>
          </div>
        </div>
      </div>
    </nav>
  );
}
